// Worker pool for parallel pathfinding
let pathfindingWorkers = [];

export function createPathfindingWorker() {
    const workerCode = `
        function countStepsInPath(gridData, startPos, endPos, excludePath, returnPath, selectedPath) {
            const queue = [{pos: startPos, steps: 0, visited: new Set()}];
            const visited = new Set();

            while (queue.length > 0) {
                const {pos, steps, visited: pathVisited} = queue.shift();
                const posKey = pos.row + ',' + pos.col;

                if (visited.has(posKey)) continue;
                visited.add(posKey);

                if (pos.row === endPos.row && pos.col === endPos.col) {
                    if (returnPath) {
                        const path = new Set(pathVisited);
                        path.add(posKey);
                        return {steps, path};
                    }
                    return steps;
                }

                const directions = [{row: -1, col: 0}, {row: 1, col: 0}, {row: 0, col: -1}, {row: 0, col: 1}];

                for (const dir of directions) {
                    const newRow = pos.row + dir.row;
                    const newCol = pos.col + dir.col;
                    const newPosKey = newRow + ',' + newCol;

                    if (newRow < 0 || newRow >= (gridData.rows || gridData.size) || newCol < 0 || newCol >= (gridData.cols || gridData.size)) continue;
                    if (pathVisited.has(newPosKey)) continue;
                    if (excludePath && excludePath.has(newPosKey) && !(newRow === endPos.row && newCol === endPos.col)) continue;

                    const room = gridData.grid[newRow][newCol];
                    if (!room || room.type === 'blocked') continue;
                    if (room.type === 'exit' && !(newRow === endPos.row && newCol === endPos.col)) continue;

                    const isInPath = selectedPath.some(p => p.row === newRow && p.col === newCol);
                    if (isInPath && !(newRow === endPos.row && newCol === endPos.col)) continue;

                    let newSteps = steps;
                    if (room.type === 'gate' || room.type === 'hazard') {
                        newSteps += 1;
                    }

                    const newVisited = new Set(pathVisited);
                    newVisited.add(posKey);
                    queue.push({pos: {row: newRow, col: newCol}, steps: newSteps, visited: newVisited});
                }
            }
            return returnPath ? Infinity : Infinity;
        }

        function findMultiplePaths(gridData, startPos, endPos, excludePath, depth, selectedPath) {
            const directions = [{row: -1, col: 0}, {row: 1, col: 0}, {row: 0, col: -1}, {row: 0, col: 1}];

            function exploreFrom(pos, visited, stepsLeft, forcedStepCost) {
                const paths = [];

                if (pos.row === endPos.row && pos.col === endPos.col) {
                    return [{steps: forcedStepCost, path: new Set(visited)}];
                }

                if (stepsLeft === 0) {
                    const pathResult = countStepsInPath(gridData, pos, endPos, visited, true, selectedPath);
                    if (pathResult !== Infinity) {
                        visited.forEach(cell => pathResult.path.add(cell));
                        pathResult.steps += forcedStepCost;
                        paths.push(pathResult);
                    }
                    return paths;
                }

                for (const dir of directions) {
                    const newRow = pos.row + dir.row;
                    const newCol = pos.col + dir.col;
                    const newKey = newRow + ',' + newCol;

                    if (newRow < 0 || newRow >= (gridData.rows || gridData.size) || newCol < 0 || newCol >= (gridData.cols || gridData.size)) continue;
                    const room = gridData.grid[newRow][newCol];
                    if (!room || room.type === 'blocked') continue;
                    if (visited.has(newKey)) continue;
                    if (excludePath && excludePath.has(newKey)) continue;
                    if (selectedPath.some(p => p.row === newRow && p.col === newCol)) continue;

                    const stepCost = (room.type === 'gate' || room.type === 'hazard') ? 1 : 0;
                    const newVisited = new Set(visited);
                    newVisited.add(newKey);
                    const subPaths = exploreFrom({row: newRow, col: newCol}, newVisited, stepsLeft - 1, forcedStepCost + stepCost);
                    paths.push(...subPaths);
                }
                return paths;
            }

            const startKey = startPos.row + ',' + startPos.col;
            return exploreFrom(startPos, new Set([startKey]), depth, 0);
        }

        function canReachRoomAndExit(currentPos, targetPos, exitPos, remainingSteps, targetRoom, gridData, selectedPath) {
            const excludeForTargetPath = new Set();
            
            if (targetPos.row !== exitPos.row || targetPos.col !== exitPos.col) {
                excludeForTargetPath.add(exitPos.row + ',' + exitPos.col);
            }

            const depth = remainingSteps <= 9 ? remainingSteps : 9;
            const pathsToTarget = findMultiplePaths(gridData, currentPos, targetPos, excludeForTargetPath, depth, selectedPath);

            if (pathsToTarget.length === 0) {
                return { canReach: false };
            }

            const excludedFromSelectedPath = new Set();
            selectedPath.forEach(step => {
                excludedFromSelectedPath.add(step.row + ',' + step.col);
            });

            for (const pathToTarget of pathsToTarget) {
                const stepsToReachTarget = pathToTarget.steps;
                if (stepsToReachTarget > remainingSteps) continue;

                const excludedForExitCheck = new Set(excludedFromSelectedPath);
                pathToTarget.path.forEach(cell => excludedForExitCheck.add(cell));
                excludedForExitCheck.delete(targetPos.row + ',' + targetPos.col);

                const stepsFromTargetToExit = countStepsInPath(gridData, targetPos, exitPos, excludedForExitCheck, false, selectedPath);
                const totalStepsNeeded = stepsToReachTarget + stepsFromTargetToExit;

                if (stepsFromTargetToExit !== Infinity && totalStepsNeeded <= remainingSteps) {
                    return { canReach: true };
                }
            }
            return { canReach: false };
        }

        self.onmessage = function(e) {
            const { gridData, currentPos, exitPos, remainingSteps, cellsToCheck, selectedPath, calculationId } = e.data;
            const dimmedCells = [];

            for (const cell of cellsToCheck) {
                const { row, col, room } = cell;
                const result = canReachRoomAndExit(currentPos, { row, col }, exitPos, remainingSteps, room, gridData, selectedPath);
                if (!result.canReach) {
                    dimmedCells.push({ row, col });
                }
            }

            self.postMessage({ dimmedCells, calculationId });
        };
    `;

    const blob = new Blob([workerCode], { type: 'application/javascript' });
    return new Worker(URL.createObjectURL(blob));
}

export function isPathfindingInitialized() {
    return pathfindingWorkers.length > 0;
}

export function initPathfindingWorkers() {
    const workerCount = navigator.hardwareConcurrency || 4;
    pathfindingWorkers = [];

    for (let i = 0; i < workerCount; i++) {
        pathfindingWorkers.push(createPathfindingWorker());
    }
}

export function getPathfindingWorkers() {
    return pathfindingWorkers;
}
